import { useState, type ReactNode } from 'react';
import { ChevronDown, History, Save, Undo2, Upload } from 'lucide-react';
import type { VersionSource } from '@reflex/shared';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { formatRelativeTime } from '../../lib/format';
import { cn } from '../../lib/utils';

const PAGE_SIZE = 20;

const REVEAL_MOTION =
  'translate-x-1 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100 motion-reduce:translate-x-0 motion-reduce:transition-none [@media(hover:none)]:translate-x-0 [@media(hover:none)]:opacity-100';

/** Timestamp: fade in on row hover/focus. Always shown on touch. */
const TIME_REVEAL_CLASS = REVEAL_MOTION;

/**
 * Action cluster: same fade. Idle pointer-events-none so invisible
 * buttons cannot steal clicks; group-hover enables :hover on the buttons.
 */
const ACTION_REVEAL_CLASS = cn(
  REVEAL_MOTION,
  'pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto [@media(hover:none)]:pointer-events-auto',
  '[&_button]:text-muted-foreground [&_button]:hover:bg-muted [&_button]:hover:text-foreground',
);

export type VersionsMenuVersion = {
  id: string;
  versionNumber: number;
  createdAt: number;
  note: string | null;
  createdBySource: VersionSource;
};

export type VersionsMenuProps<T extends VersionsMenuVersion = VersionsMenuVersion> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: readonly T[];
  isLoading?: boolean;
  publishedVersionId?: string | null;
  isBusy?: boolean;
  saveNote: string;
  onSaveNoteChange: (note: string) => void;
  onSave: () => void;
  onPromote: (versionId: string) => void;
  onRestore: (versionId: string) => void;
  /** When false, hide the chevron on the Versions trigger. */
  showChevron?: boolean;
  triggerClassName?: string;
  notePlaceholder?: string;
  noteInputId?: string;
  testIdPrefix: string;
  /** Replaces the default note line (inline rename). */
  renderNote?: (version: T) => ReactNode;
  /** Extra per-row actions below Promote/Restore. */
  renderExtraActions?: (version: T) => ReactNode;
  /** Sibling content outside the popover. */
  children?: ReactNode;
};

export function VersionsMenu<T extends VersionsMenuVersion = VersionsMenuVersion>({
  open,
  onOpenChange,
  versions,
  isLoading = false,
  publishedVersionId,
  isBusy = false,
  saveNote,
  onSaveNoteChange,
  onSave,
  onPromote,
  onRestore,
  showChevron = true,
  triggerClassName,
  notePlaceholder = 'Optional note',
  noteInputId = 'versions-menu-note',
  testIdPrefix,
  renderNote,
  renderExtraActions,
  children,
}: VersionsMenuProps<T>) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visible = versions.slice(0, visibleCount);
  const hasMore = versions.length > visible.length;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(showChevron ? undefined : 'gap-1.5', triggerClassName)}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <History className={cn(showChevron ? 'mr-1 h-4 w-4' : 'h-3.5 w-3.5')} />
          Versions
          {showChevron ? <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-60" /> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid={`${testIdPrefix}-popover`}>
        <form
          className="flex flex-col gap-2 border-b border-border/60 p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (isBusy) return;
            onSave();
          }}
        >
          <Label htmlFor={noteInputId} className="text-xs text-muted-foreground">
            Save current draft as version
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id={noteInputId}
              placeholder={notePlaceholder}
              value={saveNote}
              onChange={(e) => onSaveNoteChange(e.target.value)}
              data-testid={`${testIdPrefix}-note`}
            />
            <Button type="submit" size="sm" disabled={isBusy} data-testid={`${testIdPrefix}-save`}>
              <Save className="mr-1 h-4 w-4" /> Save
            </Button>
          </div>
        </form>

        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
              No saved versions yet. Save the current draft to start a history.
            </p>
          ) : (
            <ul className="flex flex-col">
              {visible.map((v) => {
                const isLive = publishedVersionId === v.id;
                const hasNoteLine = Boolean(renderNote || v.note);
                return (
                  <li
                    key={v.id}
                    className="group relative border-b border-border/40 px-3 py-2 last:border-b-0"
                    data-testid={`${testIdPrefix}-row-${v.id}`}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="flex min-w-0 items-center gap-1.5 pr-32 [@media(hover:hover)_and_(pointer:fine)]:pr-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:pr-32 [@media(hover:hover)_and_(pointer:fine)]:group-focus-within:pr-32">
                        <span className="text-sm font-medium">v{v.versionNumber}</span>
                        {isLive ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400"
                            data-testid={`${testIdPrefix}-live-${v.id}`}
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Live
                          </span>
                        ) : null}
                        <span
                          className={cn(
                            'truncate text-xs text-muted-foreground',
                            hasNoteLine && TIME_REVEAL_CLASS,
                          )}
                        >
                          {formatRelativeTime(v.createdAt)}
                        </span>
                      </div>
                      {renderNote ? (
                        renderNote(v)
                      ) : v.note ? (
                        <p className="line-clamp-2 text-xs text-muted-foreground">{v.note}</p>
                      ) : null}
                    </div>
                    <div
                      className={cn(
                        'absolute top-2 right-3 flex items-center',
                        ACTION_REVEAL_CLASS,
                      )}
                    >
                      {!isLive ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          disabled={isBusy}
                          onClick={() => onPromote(v.id)}
                          data-testid={`${testIdPrefix}-promote-${v.id}`}
                        >
                          <Upload /> Promote
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        disabled={isBusy}
                        onClick={() => onRestore(v.id)}
                        data-testid={`${testIdPrefix}-restore-${v.id}`}
                      >
                        <Undo2 /> Restore
                      </Button>
                      {renderExtraActions?.(v)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {hasMore ? (
            <div className="flex justify-center border-t border-border/40 p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                data-testid={`${testIdPrefix}-load-more`}
              >
                Show {Math.min(PAGE_SIZE, versions.length - visible.length)} more
              </Button>
            </div>
          ) : null}
        </div>
      </PopoverContent>
      {children}
    </Popover>
  );
}
